import { useCallback, useEffect, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Button, CircularProgress, Grid, Typography } from '@mui/material'
import Bugsnag from '@bugsnag/js'
import { AxiosError } from 'axios'
import { MdArrowBack } from 'react-icons/md'
import { client } from '../utils/client'
import { MarketplacePayloadType, MarketplaceThemeType } from '../utils/sharedTypes'
import { CartBar, CatalogSections, MarketplaceHero, MarketplaceShell, ProductDetailModal, ThemeCard } from '../components'
import { toCartItem } from '../components/marketplace/marketplaceCatalog'
import { readVisitorId, resolveDisplayDomain, hidesPrice, restrictToFunder } from '../utils/marketplaceColdStart'
import { useCart } from '../hooks/useCart'

// Marketplace d'un groupe déjà créé : le profil (profession, domaine) est déduit de ses inscriptions.
// L'arrivée à froid, sans groupe, est servie par `MarketplaceColdStart.tsx` à partir du même catalogue.

const Marketplace = () => {
  const { group_global_id } = useParams<{ group_global_id: string }>()
  const navigate = useNavigate()
  const [payload, setPayload] = useState<MarketplacePayloadType | null>(null)
  // "not_found" : groupe inconnu (404). "forbidden" : groupe non éligible (contient une
  // PreConfiguredRegistration -- ne devrait pas arriver via le CTA, qui est masqué dans ce cas, mais peut
  // survenir sur navigation directe). "network" : erreur transitoire, on propose de réessayer.
  const [error, setError] = useState<'not_found' | 'forbidden' | 'network' | null>(null)
  const [openedTheme, setOpenedTheme] = useState<MarketplaceThemeType | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  const cart = useCart(group_global_id)
  const location = useLocation()

  const fetchMarketplace = useCallback(() => {
    if (!group_global_id) return
    setError(null)
    client
      .get(`/registration/registration_group/${group_global_id}/marketplace/`)
      .then((resp) => {
        setPayload(resp.data as MarketplacePayloadType)
      })
      .catch((err: AxiosError) => {
        if (err.response?.status === 404) {
          setError('not_found')
        } else if (err.response?.status === 403) {
          setError('forbidden')
        } else {
          setError('network')
          Bugsnag.notify(err)
          console.error(err)
        }
      })
  }, [group_global_id])

  useEffect(() => {
    fetchMarketplace()
  }, [fetchMarketplace])

  const displayDomain = resolveDisplayDomain(location.pathname, payload?.domain === 'sante' ? 'sante' : 'learning')

  const handleToggleCart = (theme: MarketplaceThemeType) => {
    const item = toCartItem(theme)
    if (item) cart.toggle(item)
  }

  const handleContinue = () => {
    if (!group_global_id || cart.items.length === 0) return
    setSubmitting(true)
    setSubmitError(false)

    // Le financeur n'est volontairement pas fixé ici : son choix reste l'étape `/financement` du
    // funnel, dont dépend le calcul des étapes. `perform_create` préremplit depuis le groupe.
    // Exception, un lot IPERIA autonome : ses dossiers naissent dans le tunnel IPERIA, via `start/`.
    const request = payload?.locked_funder
      ? client.post('/registration/registration_group/start/', {
          product_codes: cart.items.map((item) => item.productCode),
          registration_group: group_global_id,
          funder: payload.locked_funder,
          domain: payload.domain,
          visitor_id: readVisitorId(),
        })
      : Promise.all(
          cart.items.map((item) =>
            client.post('/registration/', {
              product: { product_code: item.productCode },
              registration_group: { global_id: group_global_id },
            }),
          ),
        )
    request
      .then(() => {
        cart.clear()
        navigate(`/groupe/${group_global_id}`)
      })
      .catch((err: AxiosError) => {
        setSubmitting(false)
        setSubmitError(true)
        Bugsnag.notify(err)
        console.error(err)
      })
  }

  // Tant que le catalogue n'est pas chargé, le financeur est inconnu : un panier IPERIA déjà rempli ne doit pas
  // afficher son total le temps de la requête.
  const hidePrice = !payload || hidesPrice(payload?.locked_funder)
  const isAlreadyRegistered = (theme: MarketplaceThemeType | null): boolean =>
    Boolean(theme?.default_product && payload?.registered_product_codes.includes(theme.default_product.product_code))

  const renderThemeCard = (theme: MarketplaceThemeType, featured: boolean) => (
    <ThemeCard
      theme={theme}
      featured={featured}
      inCart={Boolean(theme.default_product && cart.has(theme.default_product.product_code))}
      alreadyRegistered={isAlreadyRegistered(theme)}
      onOpen={() => setOpenedTheme(theme)}
      onToggleCart={() => handleToggleCart(theme)}
      hidePrice={hidePrice}
    />
  )

  return (
    <MarketplaceShell displayDomain={displayDomain} cartCount={cart.count}>
      {group_global_id && (
        <Button component={RouterLink} to={`/groupe/${group_global_id}`} startIcon={<MdArrowBack />} sx={{ mb: 3 }}>
          Retour à mes inscriptions
        </Button>
      )}

      {error === 'not_found' && (
        <Grid container justifyContent="center">
          <Typography variant="h5" align="center">
            Ce lien n'est plus valide.
          </Typography>
        </Grid>
      )}

      {error === 'forbidden' && (
        <Grid container justifyContent="center">
          <Typography variant="h6" align="center">
            L'ajout d'une formation pour ce dossier se fait avec votre conseiller.
          </Typography>
        </Grid>
      )}

      {error === 'network' && (
        <Grid container direction="column" alignItems="center" spacing={2}>
          <Grid item>
            <Typography variant="h6" align="center">
              Une erreur est survenue pendant le chargement des formations.
            </Typography>
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={fetchMarketplace}>
              Réessayer
            </Button>
          </Grid>
        </Grid>
      )}

      {!error && !payload && (
        <Grid container justifyContent="center">
          <CircularProgress />
        </Grid>
      )}

      {!error && payload && group_global_id && (
        <>
          <MarketplaceHero
            onDark={displayDomain === 'learning'}
            title="Composez votre parcours de formation"
            subtitle={`Profitez de vos financements pour vous former sur plusieurs thèmes. Nos apprenants en retiennent ${cart.target} en moyenne.`}
            selectedCount={cart.count}
            target={cart.target}
          />
          <CatalogSections themes={restrictToFunder(payload.themes, payload.locked_funder)} renderThemeCard={renderThemeCard} />
        </>
      )}

      <ProductDetailModal
        theme={openedTheme}
        open={Boolean(openedTheme)}
        inCart={Boolean(openedTheme?.default_product && cart.has(openedTheme.default_product.product_code))}
        alreadyRegistered={isAlreadyRegistered(openedTheme)}
        onClose={() => setOpenedTheme(null)}
        onToggleCart={() => {
          if (openedTheme) handleToggleCart(openedTheme)
        }}
        hidePrice={hidePrice}
      />

      <CartBar
        hidePrice={hidePrice}
        items={cart.items}
        target={cart.target}
        submitting={submitting}
        error={submitError}
        onRemove={cart.remove}
        onContinue={handleContinue}
      />
    </MarketplaceShell>
  )
}

export default Marketplace
